import React, { useEffect, useState } from "react";

import InlineLoader from "./InlineLoader";
import { getExpectedFundsCharges } from "services/billing";
import { TableComponent } from "./ui";
import { TierPill, TierCard } from "./ExpectedFundsShared";
import {
  EXPECTED_FUNDS_TIERS,
  ExpectedFundsChargeRow,
  ExpectedFundsChargesResponse,
  formatMoney,
  formatBankDate,
  formatPaymentMethod,
} from "./types";

interface Props {
  date: string;
  onBack: () => void;
}

const subscriptionLabel = (row: ExpectedFundsChargeRow): string => {
  const plan = row.subscription?.planName;
  if (!plan) return "—";
  const seats = row.subscription?.seats;
  return seats != null ? `${plan} — ${seats} seats` : plan;
};

const columns = [
  {
    id: "customerName",
    label: "Customer",
    render: (_v: unknown, row: ExpectedFundsChargeRow) => (
      <div className="flex flex-col">
        <span className="para-body-s-medium color-text-heading-primary">{row.customerName || "—"}</span>
        {row.stripeCustomerId ? (
          <span className="para-body-xs-regular color-text-body-secondary">{row.stripeCustomerId}</span>
        ) : null}
      </div>
    ),
  },
  { id: "amountCents", label: "Amount", render: (v: number) => formatMoney(v) },
  {
    id: "paymentMethod",
    label: "Payment method",
    render: (_v: unknown, row: ExpectedFundsChargeRow) => formatPaymentMethod(row.paymentMethod),
  },
  {
    id: "subscription",
    label: "Subscription",
    render: (_v: unknown, row: ExpectedFundsChargeRow) => subscriptionLabel(row),
  },
  { id: "chargeDate", label: "Charge date", render: (v: string | null) => formatBankDate(v) },
  {
    id: "tier",
    label: "Confidence",
    render: (v: ExpectedFundsChargeRow["tier"]) => <TierPill tier={v} />,
  },
];

/**
 * [PRD-2043] Drill-down for a single bank date — every charge landing that day
 * (projected + confirmed), with the per-tier summary. Rendered in place of the
 * forecast list; `onBack` returns to it.
 */
export default function ExpectedFundsCharges({ date, onBack }: Props) {
  const [data, setData] = useState<ExpectedFundsChargesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await getExpectedFundsCharges(date);
        if (active) setData(res);
      } catch {
        if (active) setError("Failed to load charges for this date.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [date]);

  const title = data
    ? `${data.isEstimated ? "~ est. " : ""}${formatBankDate(data.date, true)} · ${formatMoney(data.totalCents)}`
    : formatBankDate(date, true);

  const dayTotal = data
    ? data.breakdown.settledCents +
      data.breakdown.inTransitCents +
      data.breakdown.pendingCents +
      data.breakdown.projectedCents
    : 0;

  return (
    <div className="flex flex-col gap-1">
      <button type="button" className="ef-backlink para-body-s-medium color-text-body-secondary" onClick={onBack}>
        ‹ Expected funds
      </button>
      <p className="heading-h3-semibold color-text-heading-primary">{title}</p>

      {loading ? (
        <InlineLoader />
      ) : error ? (
        <p className="para-body-m-regular color-text-body-secondary">{error}</p>
      ) : data ? (
        <>
          <p className="para-body-s-regular color-text-body-secondary">
            {data.chargeCount} charge{data.chargeCount === 1 ? "" : "s"}
            {data.receivedCents > 0 ? ` · ${formatMoney(data.receivedCents)} of this date already received` : ""}
            {` · arrival date ${data.isEstimated ? "is estimated" : "confirmed"}`}
          </p>

          <div className="ef-tier-cards">
            {EXPECTED_FUNDS_TIERS.filter((t) => data.breakdown[t.amountKey] > 0).map((t) => (
              <TierCard
                key={t.key}
                tier={t.key}
                amountCents={data.breakdown[t.amountKey]}
                caption={`${dayTotal > 0 ? Math.round((data.breakdown[t.amountKey] / dayTotal) * 100) : 0}% of the day`}
              />
            ))}
          </div>

          <TableComponent columns={columns} data={data.rows} disableSort />
        </>
      ) : null}
    </div>
  );
}
